/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Actions for Settings Profile component
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.3.0
 */

import { createAction } from '@modules/app/createAction';

import {
    SETTINGS_PROFILE_INIT_ACTION,
    SETTINGS_PROFILE_LOAD_META_ACTION,
    SETTINGS_PROFILE_LOAD_META_FAILURE,
    SETTINGS_PROFILE_LOAD_META_SUCCESS,
    SETTINGS_PROFILE_RESET_PARAMS_ACTION,
    SETTINGS_PROFILE_SAVE_COMMON_ACTION,
    SETTINGS_PROFILE_SAVE_COMMON_FAILURE,
    SETTINGS_PROFILE_SAVE_COMMON_SUCCESS,
    SETTINGS_PROFILE_SAVE_PASSWORD_ACTION,
    SETTINGS_PROFILE_SAVE_PASSWORD_FAILURE,
    SETTINGS_PROFILE_SAVE_PASSWORD_SUCCESS,
    SETTINGS_PROFILE_SAVE_QUESTION_ACTION,
    SETTINGS_PROFILE_SAVE_QUESTION_FAILURE,
    SETTINGS_PROFILE_SAVE_QUESTION_SUCCESS,
    SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_UPDATED_ACTION,
    SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_VALID_ACTION,
    SETTINGS_PROFILE_SET_IS_PREFERRED_LK_PARAMS_UPDATED_ACTION,
    SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
    SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION,
} from './settingsProfileConstants';
import {
    ISettingsProfileLoadMetaFailurePayload,
    ISettingsProfileLoadMetaSuccessPayload,
    ISettingsProfileResetParamsActionPayload,
    ISettingsProfileSaveCommonFailurePayload,
    ISettingsProfileSavePasswordFailurePayload,
    ISettingsProfileSaveQuestionFailurePayload,
    ISettingsProfileSaveQuestionSuccessPayload,
    ISettingsProfileSetIsCommonParamsUpdatedActionPayload,
    ISettingsProfileSetIsCommonParamsValidActionPayload,
    ISettingsProfileSetIsPreferredLKParamsUpdatedActionPayload,
    TSettingsProfileInitAction,
    TSettingsProfileLoadMetaAction,
    TSettingsProfileLoadMetaFailure,
    TSettingsProfileLoadMetaSuccess,
    TSettingsProfileResetParamsAction,
    TSettingsProfileSaveCommonAction,
    TSettingsProfileSaveCommonFailure,
    TSettingsProfileSaveCommonSuccess,
    TSettingsProfileSavePasswordAction,
    TSettingsProfileSavePasswordFailure,
    TSettingsProfileSavePasswordSuccess,
    TSettingsProfileSaveQuestionAction,
    TSettingsProfileSaveQuestionFailure,
    TSettingsProfileSaveQuestionSuccess,
    TSettingsProfileSetIsCommonParamsUpdatedAction,
    TSettingsProfileSetIsCommonParamsValidAction,
    TSettingsProfileSetIsPreferredLKParamsUpdatedAction,
    TSettingsProfileSetParamComponentAction,
    TSettingsProfileSetParamComponentActionPayload,
    TSettingsProfileSetParamContactsAction,
    TSettingsProfileSetParamContactsActionPayload,
    TSettingsProfileSetParamPasswordAction,
    TSettingsProfileSetParamPasswordActionPayload,
    TSettingsProfileSetParamPreferredLKAction,
    TSettingsProfileSetParamPreferredLKActionPayload,
    TSettingsProfileSetParamQuestionAction,
    TSettingsProfileSetParamQuestionActionPayload,
} from './settingsProfileTypes';

/*
 * -------------------------------------------
 *           PROFILE INIT
 * -------------------------------------------
 */

export const settingsProfileInitAction = (): TSettingsProfileInitAction => createAction(SETTINGS_PROFILE_INIT_ACTION);

/*
 * -------------------------------------------
 *           LOAD META
 * -------------------------------------------
 */

export const settingsProfileLoadMetaAction = (): TSettingsProfileLoadMetaAction =>
    createAction(SETTINGS_PROFILE_LOAD_META_ACTION);
export const settingsProfileLoadMetaSuccess = (
    payload: ISettingsProfileLoadMetaSuccessPayload,
): TSettingsProfileLoadMetaSuccess => createAction(SETTINGS_PROFILE_LOAD_META_SUCCESS, payload);
export const settingsProfileLoadMetaFailure = (
    payload: ISettingsProfileLoadMetaFailurePayload,
): TSettingsProfileLoadMetaFailure => createAction(SETTINGS_PROFILE_LOAD_META_FAILURE, payload);

/*
 * -------------------------------------------
 *           SET IS UPDATED
 * -------------------------------------------
 */

export const settingsProfileSetIsParamsUpdatedAction = (
    payload: ISettingsProfileSetIsCommonParamsUpdatedActionPayload,
): TSettingsProfileSetIsCommonParamsUpdatedAction =>
    createAction(SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_UPDATED_ACTION, payload);

export const settingsProfileSetIsParamsPreferredLKUpdatedAction = (
    payload: ISettingsProfileSetIsPreferredLKParamsUpdatedActionPayload,
): TSettingsProfileSetIsPreferredLKParamsUpdatedAction =>
    createAction(SETTINGS_PROFILE_SET_IS_PREFERRED_LK_PARAMS_UPDATED_ACTION, payload);

/*
 * -------------------------------------------
 *           SET PARAMS IS VALID
 * -------------------------------------------
 */

export const settingsProfileSetIsCommonParamsValidAction = (
    payload: ISettingsProfileSetIsCommonParamsValidActionPayload,
): TSettingsProfileSetIsCommonParamsValidAction =>
    createAction(SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_VALID_ACTION, payload);

/*
 * -------------------------------------------
 *           SET PARAM
 * -------------------------------------------
 */

export const settingsProfileSetParamContactsAction = (
    payload: TSettingsProfileSetParamContactsActionPayload,
): TSettingsProfileSetParamContactsAction => createAction(SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION, payload); //

export const settingsProfileSetParamPreferredLKAction = (
    payload: TSettingsProfileSetParamPreferredLKActionPayload,
): TSettingsProfileSetParamPreferredLKAction => createAction(SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION, payload);

export const settingsProfileSetParamComponentsAction = (
    payload: TSettingsProfileSetParamComponentActionPayload,
): TSettingsProfileSetParamComponentAction => createAction(SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION, payload);

export const settingsProfileSetParamPasswordAction = (
    payload: TSettingsProfileSetParamPasswordActionPayload,
): TSettingsProfileSetParamPasswordAction => createAction(SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION, payload);

export const settingsProfileSetParamQuestionAction = (
    payload: TSettingsProfileSetParamQuestionActionPayload,
): TSettingsProfileSetParamQuestionAction => createAction(SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION, payload);

export const settingsProfileResetParamsAction = (
    payload: ISettingsProfileResetParamsActionPayload,
): TSettingsProfileResetParamsAction => createAction(SETTINGS_PROFILE_RESET_PARAMS_ACTION, payload);

/*
 * -------------------------------------------
 *           SAVE CHANGES
 * -------------------------------------------
 */

export const settingsProfileSaveCommonAction = (): TSettingsProfileSaveCommonAction =>
    createAction(SETTINGS_PROFILE_SAVE_COMMON_ACTION);
export const settingsProfileSaveCommonSuccess = (): TSettingsProfileSaveCommonSuccess =>
    createAction(SETTINGS_PROFILE_SAVE_COMMON_SUCCESS);
export const settingsProfileSaveCommonFailure = (
    payload: ISettingsProfileSaveCommonFailurePayload,
): TSettingsProfileSaveCommonFailure => createAction(SETTINGS_PROFILE_SAVE_COMMON_FAILURE, payload);

export const settingsProfileSavePasswordAction = (): TSettingsProfileSavePasswordAction =>
    createAction(SETTINGS_PROFILE_SAVE_PASSWORD_ACTION);
export const settingsProfileSavePasswordSuccess = (): TSettingsProfileSavePasswordSuccess =>
    createAction(SETTINGS_PROFILE_SAVE_PASSWORD_SUCCESS);
export const settingsProfileSavePasswordFailure = (
    payload: ISettingsProfileSavePasswordFailurePayload,
): TSettingsProfileSavePasswordFailure => createAction(SETTINGS_PROFILE_SAVE_PASSWORD_FAILURE, payload);

export const settingsProfileSaveQuestionAction = (): TSettingsProfileSaveQuestionAction =>
    createAction(SETTINGS_PROFILE_SAVE_QUESTION_ACTION);
export const settingsProfileSaveQuestionSuccess = (
    payload: ISettingsProfileSaveQuestionSuccessPayload,
): TSettingsProfileSaveQuestionSuccess => createAction(SETTINGS_PROFILE_SAVE_QUESTION_SUCCESS, payload);
export const settingsProfileSaveQuestionFailure = (
    payload: ISettingsProfileSaveQuestionFailurePayload,
): TSettingsProfileSaveQuestionFailure => createAction(SETTINGS_PROFILE_SAVE_QUESTION_FAILURE, payload);
